import { Controller } from "@hotwired/stimulus"

// The bell/sidebar quick view. Sits on the trigger link: a plain click opens the panel
// instead of navigating, while a modified or middle click still opens the page.
//
// The panel is rendered once at the end of <body> (notifications/_preview_panel) and
// found by id, because inside the sidebar's <nav> the collapsed-rail CSS would restyle
// every link in it. Its frame reloads on each open, so the list is never stale.
export default class extends Controller {
  static values = {
    panel: String,
    src: String,
    placement: { type: String, default: "right" },
  }

  connect() {
    this.clickOutside = this.clickOutside.bind(this)
    this.keydown = this.keydown.bind(this)
    this.close = this.close.bind(this)
    document.addEventListener("click", this.clickOutside)
    document.addEventListener("keydown", this.keydown)
    document.addEventListener("turbo:before-visit", this.close)
  }

  disconnect() {
    this.close()
    document.removeEventListener("click", this.clickOutside)
    document.removeEventListener("keydown", this.keydown)
    document.removeEventListener("turbo:before-visit", this.close)
  }

  toggle(event) {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0)
      return
    if (!this.panel) return

    event.preventDefault()
    this.isOpen() ? this.close() : this.open()
  }

  open() {
    const panel = this.panel
    panel.hidden = false
    this.position(panel)
    this.element.setAttribute("aria-expanded", "true")

    const frame = panel.querySelector("turbo-frame")
    if (!frame) return
    // Placed while it still shows the spinner; the loaded list is taller, so place it
    // again or it runs off the bottom of the screen.
    frame.addEventListener("turbo:frame-load", () => this.position(panel), { once: true })
    if (frame.getAttribute("src")) {
      frame.reload()
    } else {
      frame.setAttribute("src", this.srcValue)
    }
  }

  close() {
    const panel = this.panel
    if (!panel || panel.hidden) return

    panel.hidden = true
    this.element.setAttribute("aria-expanded", "false")
  }

  // private

  get panel() {
    return document.getElementById(this.panelValue)
  }

  isOpen() {
    return this.panel && !this.panel.hidden
  }

  position(panel) {
    const trigger = this.element.getBoundingClientRect()
    const margin = 8
    if (this.placementValue === "bottom-end") {
      panel.style.top = `${trigger.bottom + margin}px`
      panel.style.left = `${Math.max(margin, trigger.right - panel.offsetWidth)}px`
    } else {
      // Beside the sidebar, not over it: the entry itself ends inside the sidebar's padding.
      const edge =
        this.element.closest(".sidebar-root")?.getBoundingClientRect().right ?? trigger.right
      const maxTop = window.innerHeight - panel.offsetHeight - margin
      panel.style.left = `${edge + margin}px`
      panel.style.top = `${Math.max(margin, Math.min(trigger.top, maxTop))}px`
    }
  }

  // A click on a link inside the panel leaves it: close, so a drawer opened from a row
  // is not covered by the panel.
  clickOutside(event) {
    if (!this.isOpen()) return
    if (this.element.contains(event.target)) return
    if (this.panel.contains(event.target) && !event.target.closest("a")) return

    this.close()
  }

  keydown(event) {
    if (event.key === "Escape") this.close()
  }
}
